import { useNavigationBarQuery } from "@crowdcoursing/api/graphql";
import { type ProspectDto } from "@crowdcoursing/api/openapi";
import { Box, CircularProgress, Tab, Tabs, Typography } from "@mui/material";
import { useState } from "react";

import { useTaskDetails } from "~hooks/useTaskDetails";
import { useSelector } from "~hooks/useTypedRedux";
import { AddContactModal } from "~ui/molecules/AddContactModal/AddContactModal";
import { AddInteractionModal } from "~ui/molecules/AddInteractionModal/AddInteractionModal";
import { AddNoteModal } from "~ui/molecules/AddNoteModal/AddNoteModal";
import { AddTaskModal } from "~ui/molecules/AddTaskModal/AddTaskModal";
import { ProspectHeader } from "~ui/molecules/ProspectHeader/ProspectHeader";
import { ActivityTab } from "~ui/molecules/ProspectTabs/Activity/ActivityTab";
import { OverviewTab } from "~ui/molecules/ProspectTabs/Overview/OverviewTab";
import { PeopleTab } from "~ui/molecules/ProspectTabs/People/PeopleTab";
import { TasksTab } from "~ui/molecules/ProspectTabs/Tasks/TasksTab";
import { useContactModal } from "~ui/pages/SalesPortal/useContactModal";
import { useInteractionModal } from "~ui/pages/SalesPortal/useInteractionModal";
import { useNoteModal } from "~ui/pages/SalesPortal/useNoteModal";
import { useProspectDetails } from "~ui/pages/SalesPortal/useProspectDetails";
import { useTaskModal } from "~ui/pages/SalesPortal/useTaskModal";
import { type CarouselTaskItem } from "~ui/templates/TaskDashboardTemplate/taskUtils";

const tLoading = "Loading prospect details...";
const tNotFound = "Prospect details not found";
const tNoTaskSelected = "Select a task to view details";

const tOverview = "Overview";
const tPeople = "People";
const tActivity = "Activity";
const tTasks = "Tasks";

type TabValue = "overview" | "people" | "activity" | "tasks";

const TABS: { label: string; value: TabValue }[] = [
  { label: tOverview, value: "overview" },
  { label: tPeople, value: "people" },
  { label: tActivity, value: "activity" },
  { label: tTasks, value: "tasks" },
];

export interface SalesTaskPreviewTemplateProps {
  selectedTask: CarouselTaskItem | null;
}

export const SalesTaskPreviewTemplate = ({ selectedTask }: SalesTaskPreviewTemplateProps) => {
  // First, get the task details using the task ID
  const taskId = selectedTask?.id || "";

  // Use our new hook to get both task and prospect details
  const { prospect, isLoading } = useTaskDetails(taskId);

  // Get the prospect ID from the task data
  const prospectId = prospect?.id || "";

  // Use the same hooks as ProspectDetails for updating prospect data
  const { updateField, changeStage, assignUser, unassignUser, reassignOpenTasksDialog } =
    useProspectDetails(prospectId);

  // Get user roles from NavigationBar query
  const { data: navigationData } = useNavigationBarQuery();

  // Manage tab state
  const [activeTab, setActiveTab] = useState<TabValue>("overview");

  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: TabValue) => {
    setActiveTab(newValue);
  };

  // Use the same modal hooks as ProspectDetails, passing the same prospectId
  const contactModal = useContactModal(prospectId);
  const noteModal = useNoteModal(prospectId);
  const interactionModal = useInteractionModal(prospectId);
  const taskModal = useTaskModal(prospectId);

  const currentUserId = useSelector((state) => state.user?.id);

  const handleAssignToMe = async () => {
    if (currentUserId) {
      await assignUser(currentUserId);
    }
  };

  const handleUnassignMe = async () => {
    if (currentUserId) {
      await unassignUser(currentUserId);
    }
  };

  // If no task is selected, show a message
  if (!selectedTask) {
    return (
      <Box
        sx={{
          alignItems: "center",
          bgcolor: "background.paper",
          borderRadius: 1,
          boxShadow: 1,
          display: "flex",
          height: "300px",
          justifyContent: "center",
        }}
      >
        <Typography variant="body1" color="text.secondary">
          {tNoTaskSelected}
        </Typography>
      </Box>
    );
  }

  // If the selected task is not a sales task, don't show anything
  if (selectedTask.type !== "sales") {
    return null;
  }

  return (
    <>
      {isLoading ? (
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            height: "300px",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
          <Typography>{tLoading}</Typography>
        </Box>
      ) : prospect ? (
        <Box display="flex" flexDirection="column" gap={3}>
          {/* Prospect Header */}
          <ProspectHeader
            prospect={prospect}
            onFieldUpdate={updateField}
            onStageChange={changeStage}
            onAddContact={() => {
              contactModal.openAddModal();
              setActiveTab("people");
            }}
            onAddNote={() => {
              noteModal.openAddModal();
              setActiveTab("activity");
            }}
            onAddInteraction={() => {
              interactionModal.openAddModal();
              setActiveTab("activity");
            }}
            onAddTask={() => {
              taskModal.openAddModal();
              setActiveTab("tasks");
            }}
          />

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs value={activeTab} onChange={handleTabChange}>
              {TABS.map((tab) => (
                <Tab key={tab.value} label={tab.label} value={tab.value} />
              ))}
            </Tabs>
          </Box>

          {/* Tab Content */}
          <Box display="flex" flexDirection="column">
            {activeTab === "overview" && (
              <OverviewTab
                prospect={prospect as ProspectDto}
                onAssignToMe={handleAssignToMe}
                onUnassignMe={handleUnassignMe}
                onFieldUpdate={(field, value) => updateField(field, value)}
                roles={navigationData?.me?.roles || []}
                onAssignUser={assignUser}
                onUnassignUser={unassignUser}
              />
            )}
            {activeTab === "people" && <PeopleTab prospect={prospect as ProspectDto} contactModal={contactModal} />}
            {activeTab === "activity" && (
              <ActivityTab
                prospect={prospect as ProspectDto}
                noteModal={noteModal}
                interactionModal={interactionModal}
              />
            )}
            {activeTab === "tasks" && (
              <TasksTab prospect={prospect as ProspectDto} currentUserId={currentUserId} taskModal={taskModal} />
            )}
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            height: "300px",
            justifyContent: "center",
          }}
        >
          <Typography color="error">{tNotFound}</Typography>
        </Box>
      )}

      {/* Contact Modals */}
      {contactModal.isAddOpen && contactModal.handleAddContact && (
        <AddContactModal
          open={contactModal.isAddOpen}
          onClose={contactModal.closeAddModal}
          onSubmit={contactModal.handleAddContact}
        />
      )}

      {contactModal.editContact && contactModal.handleUpdateContact && (
        <AddContactModal
          open={true}
          onClose={contactModal.closeEditModal}
          onSubmit={contactModal.handleUpdateContact}
          initialValues={{
            email: contactModal.editContact?.email ?? undefined,
            firstName: contactModal.editContact?.firstName ?? "",
            isPrimary: contactModal.editContact?.isPrimary ?? false,
            lastName: contactModal.editContact?.lastName ?? "",
            phone: contactModal.editContact?.phone ?? undefined,
            roleTitle: contactModal.editContact?.roleTitle ?? undefined,
          }}
          isEdit
        />
      )}

      {/* Other Modals */}
      {noteModal.isAddOpen && noteModal.handleAddNote && (
        <AddNoteModal open={noteModal.isAddOpen} onClose={noteModal.closeAddModal} onSubmit={noteModal.handleAddNote} />
      )}

      {interactionModal.isAddOpen && interactionModal.handleAddInteraction && (
        <AddInteractionModal
          open={interactionModal.isAddOpen}
          onClose={interactionModal.closeAddModal}
          onSubmit={interactionModal.handleAddInteraction}
        />
      )}

      {reassignOpenTasksDialog}
      {taskModal.isAddOpen && taskModal.handleCreateTask && (
        <AddTaskModal
          open={taskModal.isAddOpen}
          onClose={taskModal.closeAddModal}
          onSubmit={taskModal.handleCreateTask}
        />
      )}
    </>
  );
};
